export const VERSION = "1.0.0";
export const ENGINE_NAME = "Axion";
export const COPYRIGHT_HTML = "Axion <span class='text-lg inline-block align-middle mx-1'>©</span> 2026. An internal system by <a href='https://rishishah.in' target='_blank' class='hover:text-white underline underline-offset-4 transition-all'>Rishi Shah</a>, made public";
export const COPYRIGHT_TEXT = "Axion © 2026. An internal system by Rishi Shah, made public";

export const FFT_SIZE = 2048;
export const SMOOTHING_CONSTANT = 0.85;

export const PARAM_CATEGORIES = {
  FORM: ['topology', 'topX', 'topY', 'topZ', 'scale', 'grid', 'symmetry', 'shapeComplexity', 'wireframe'],
  ENERGY: ['drive', 'inertia', 'impact', 'oscillation'],
  BEHAVIOR: ['warp', 'fold', 'quantize', 'resonance', 'scatter', 'fractal'],
  LIGHTING: ['ambientLight', 'directionalLight', 'pointLight', 'lightColor', 'bloom', 'roughness', 'metalness', 'emission'],
  SYSTEM: ['grain', 'chroma', 'color1', 'color2', 'depth', 'chromaticAberration', 'vignette', 'noise', 'stability']
};

export const DEFAULT_PARAMS: any = {
  topology: 0.15,
  topX: 0.5,
  topY: 0.5,
  topZ: 0.5,
  scale: 0.45,
  grid: 0.0,
  symmetry: 0.0,
  shapeComplexity: 0.5,
  wireframe: 0.0,
  drive: 0.5,
  inertia: 0.4,
  impact: 0.3,
  oscillation: 0.2,
  warp: 0.1,
  fold: 0.0,
  quantize: 0.0,
  resonance: 0.2,
  scatter: 0.0,
  fractal: 0.0,
  ambientLight: 0.5,
  directionalLight: 0.5,
  pointLight: 0.5,
  lightColor: 0.5,
  bloom: 0.2,
  roughness: 0.5,
  metalness: 0.5,
  emission: 0.2,
  grain: 0.5,
  chroma: 0.3,
  color1: 0.5,
  color2: 0.5,
  depth: 0.6,
  chromaticAberration: 0.1,
  vignette: 0.5,
  noise: 0.1,
  stability: 0.5
};

export const PREMADE_ENGINES: Record<string, any> = {
  "CORE_EVALUATOR": DEFAULT_PARAMS,
  "VOID_ENTITY": {
    ...DEFAULT_PARAMS,
    topology: 0.1, topX: 0.5, topY: 0.5, topZ: 0.5,
    scale: 0.35, grid: 0.0, symmetry: 0.8, shapeComplexity: 0.9, wireframe: 0.7,
    drive: 0.9, inertia: 0.2, impact: 0.9, oscillation: 0.1,
    warp: 0.8, fold: 0.6, quantize: 0.0, resonance: 0.9, scatter: 0.2, fractal: 0.8,
    ambientLight: 0.1, directionalLight: 0.8, pointLight: 0.9, lightColor: 0.1, bloom: 0.9, roughness: 0.2, metalness: 0.9, emission: 0.8,
    grain: 0.8, chroma: 0.9, color1: 0.1, color2: 0.9, depth: 0.8, chromaticAberration: 0.7, vignette: 0.9, noise: 0.4, stability: 0.9
  },
  "NEON_LATTICE": {
    ...DEFAULT_PARAMS,
    topology: 0.25, topX: 0.5, topY: 0.5, topZ: 0.5,
    scale: 0.3, grid: 0.85, symmetry: 0.6, shapeComplexity: 0.2, wireframe: 0.9,
    drive: 0.7, inertia: 0.5, impact: 0.6, oscillation: 0.4,
    warp: 0.2, fold: 0.4, quantize: 0.2, resonance: 0.4, scatter: 0.1, fractal: 0.3,
    ambientLight: 0.4, directionalLight: 0.6, pointLight: 0.8, lightColor: 0.8, bloom: 0.8, roughness: 0.1, metalness: 0.8, emission: 0.6,
    grain: 0.4, chroma: 0.8, color1: 0.8, color2: 0.2, depth: 0.6, chromaticAberration: 0.4, vignette: 0.6, noise: 0.2, stability: 0.8
  },
  "QUANTUM_FOAM": {
    ...DEFAULT_PARAMS,
    topology: 0.0, topX: 0.5, topY: 0.5, topZ: 0.5,
    scale: 0.2, grid: 0.9, symmetry: 0.2, shapeComplexity: 0.8, wireframe: 0.1,
    drive: 0.6, inertia: 0.1, impact: 0.8, oscillation: 0.8,
    warp: 0.9, fold: 0.9, quantize: 0.8, resonance: 0.5, scatter: 0.9, fractal: 0.6,
    ambientLight: 0.6, directionalLight: 0.4, pointLight: 0.7, lightColor: 0.5, bloom: 0.5, roughness: 0.8, metalness: 0.2, emission: 0.4,
    grain: 0.9, chroma: 0.9, color1: 0.5, color2: 0.9, depth: 0.5, chromaticAberration: 0.8, vignette: 0.7, noise: 0.9, stability: 0.4
  },
  "CHROMATIC_ABYSS": {
    ...DEFAULT_PARAMS,
    topology: 0.5, topX: 0.2, topY: 0.8, topZ: 0.5,
    scale: 0.5, grid: 0.0, symmetry: 0.4, shapeComplexity: 0.6, wireframe: 0.0,
    drive: 0.8, inertia: 0.7, impact: 0.7, oscillation: 0.2,
    warp: 0.9, fold: 0.3, quantize: 0.1, resonance: 0.7, scatter: 0.3, fractal: 0.5,
    ambientLight: 0.2, directionalLight: 0.7, pointLight: 0.5, lightColor: 0.9, bloom: 0.7, roughness: 0.4, metalness: 0.7, emission: 0.5,
    grain: 0.6, chroma: 1.0, color1: 0.0, color2: 1.0, depth: 0.9, chromaticAberration: 1.0, vignette: 0.8, noise: 0.5, stability: 0.7
  },
  "CRYSTALLINE_STRUCTURE": {
    ...DEFAULT_PARAMS,
    topology: 0.7, topX: 0.5, topY: 0.5, topZ: 0.5,
    scale: 0.4, grid: 0.2, symmetry: 0.9, shapeComplexity: 0.4, wireframe: 0.8,
    drive: 0.8, inertia: 0.8, impact: 0.9, oscillation: 0.1,
    warp: 0.1, fold: 0.5, quantize: 0.9, resonance: 0.8, scatter: 0.0, fractal: 0.4,
    ambientLight: 0.3, directionalLight: 0.9, pointLight: 0.8, lightColor: 0.6, bloom: 0.6, roughness: 0.0, metalness: 1.0, emission: 0.3,
    grain: 0.3, chroma: 0.4, color1: 0.6, color2: 0.4, depth: 0.7, chromaticAberration: 0.3, vignette: 0.5, noise: 0.1, stability: 0.9
  }
};

export const LOGO_PARAMS: any = {
  topology: 0.3,
  topX: 0.5,
  topY: 0.5,
  topZ: 0.5,
  scale: 0.5,
  grid: 0.0,
  symmetry: 0.8,
  drive: 0.0,
  inertia: 1.0,
  impact: 0.0,
  oscillation: 0.05,
  warp: 0.0,
  fold: 0.5,
  quantize: 0.9,
  resonance: 0.6,
  scatter: 0.0,
  fractal: 0.0,
  grain: 0.7,
  chroma: 0.0,
  color1: 0.5,
  color2: 0.5,
  depth: 0.8,
  stability: 1.0
};

export const PARAM_DESCRIPTIONS: Record<string, string> = {
  topology: "Interpolation between fundamental geometric manifolds via SDF-blending.",
  topX: "Independent topology modulation on X axis.",
  topY: "Independent topology modulation on Y axis.",
  topZ: "Independent topology modulation on Z axis.",
  scale: "Isotropic volumetric primitive magnitude within the viewing frustum.",
  grid: "Domain repetition frequency utilizing modular coordinate arithmetic.",
  symmetry: "Orthogonal Cartesian reflection and rotative spatial mirroring.",
  shapeComplexity: "Detail level of the geometric shape.",
  wireframe: "Wireframe thickness and blending.",
  drive: "Coupling coefficient between signal RMS and geometric displacement.",
  inertia: "Time-constant for the exponential moving average of signal features.",
  impact: "Sensitivity to the first derivative of the spectral energy density.",
  oscillation: "Deterministic angular velocity of the global phase accumulator.",
  warp: "Domain deformation using high-frequency trigonometric interference.",
  fold: "Iterative absolute-value folding of the coordinate manifold.",
  quantize: "Discretization of the spatial grid to finite bit-depth resolutions.",
  resonance: "Harmonic spatial feedback loop scale within the evaluator.",
  scatter: "Stochastic distribution of spatial coordinates.",
  fractal: "Recursive fractal iteration intensity.",
  ambientLight: "Ambient light intensity.",
  directionalLight: "Directional light intensity.",
  pointLight: "Point light intensity.",
  lightColor: "Light color hue.",
  bloom: "Bloom intensity.",
  roughness: "Material roughness.",
  metalness: "Material metalness.",
  emission: "Material emission.",
  grain: "Deterministic hash-based dither density for spatial entropy simulation.",
  chroma: "Phase-shift in the spectral color mapping during lighting evaluation.",
  color1: "Primary spectral color offset.",
  color2: "Secondary spectral color offset.",
  depth: "Exponential atmospheric attenuation based on ray-traversal distance.",
  chromaticAberration: "Chromatic aberration intensity.",
  vignette: "Vignette intensity.",
  noise: "Noise intensity.",
  stability: "Tolerance threshold for signal-induced manifold divergence."
};

export const BAYER_MATRIX_4X4 = [
  0, 8, 2, 10,
  12, 4, 14, 6,
  3, 11, 1, 9,
  15, 7, 13, 5
].map(v => v / 16.0);

export const BRANDING_SPECS = {
  LOGO_MATH: "The Axion Monolith is a minimalist hexagonal construct derived from an isometric projection of a nested cube. It represents the 'A' of Axiom while symbolizing the three core pillars of the engine: Signal Acquisition, Manifold Synthesis, and Volumetric Evaluation. The 60° internal angles ensure perfect tessellation, mirroring the engine's deterministic lattice systems.",
  COLOR_THEORY: "Neutral achromatic baseline with precision offsets. The system utilizes a 'Zero-Chroma' default to emphasize form over surface, with spectral shifts allowed only as a function of signal centroid data.",
  TYPOGRAPHY: "Sono (Monospace) is utilized for its fixed-pitch rhythmic regularity, ensuring that numerical readouts and signal telemetry maintain character alignment across all responsive viewport states.",
  ENGINE_LOGIC: "Pure mathematical determinism. Every visual artifact is a direct result of a calculated signal force. There are no stochastic processes; randomness is replaced by deterministic pseudo-noise generated via hash-based coordinate evaluation."
};